import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';
import { updateCartBubble } from './utilities.js';

const QUICK_BUY_SECTION_ID = 'product-quick-buy-form';
const QUICK_BUY_MODAL_ID = 'quick-buy-modal';

function productUrlByHandle(handle) {
  return `${window.Shopify.routes.root}products/${encodeURIComponent(handle)}`;
}

function sectionFetchUrl(handle) {
  return `${productUrlByHandle(handle)}?section_id=${QUICK_BUY_SECTION_ID}`;
}

function setTriggerLoading(trigger, loading) {
  if (!trigger) return;
  trigger.disabled = loading;
  trigger.classList.toggle('opacity-60', loading);
  trigger.classList.toggle('cursor-wait', loading);
}

async function refreshCartData() {
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    window.__cart = await response.json();
    updateCartBubble();
  } catch (_) {
    // Keep UX resilient even if cart refresh fails
  }
}

async function directAddToCart(variantId, trigger) {
  if (!variantId) return;

  setTriggerLoading(trigger, true);

  try {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });

    const result = await response.json();
    if (result?.status && result.status !== 200) return;

    await refreshCartData();
    ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
    ThemeBus.dispatchEvent(new Event(EVENTS.cartItemAdded));
  } catch (_) {
    // No-op; keep user on current page if add fails silently
  } finally {
    setTriggerLoading(trigger, false);
  }
}

function executeInjectedScripts(sectionWrapper) {
  const scripts = Array.from(sectionWrapper.querySelectorAll('script')).filter((script) => {
    const type = script.type || '';
    return type !== 'application/json';
  });

  scripts.forEach((script) => script.remove());

  const loaders = scripts.map((script) => {
    const newScript = document.createElement('script');
    if (script.type) newScript.type = script.type;

    if (script.src) {
      newScript.src = script.src;
      if (script.defer) newScript.defer = true;
      if (script.async) newScript.async = true;
      return new Promise((resolve) => {
        newScript.onload = () => resolve();
        newScript.onerror = () => resolve();
        document.head.appendChild(newScript);
      });
    }

    newScript.textContent = script.textContent;
    document.head.appendChild(newScript);
    return Promise.resolve();
  });

  return Promise.all(loaders);
}

function reinitializeInjectedElements(scope) {
  const selectors = ['quick-add-component', 'quantity-selector-component'];
  selectors.forEach((selector) => {
    scope.querySelectorAll(selector).forEach((el) => {
      if (el.initialized) return;
      if (typeof el.connectedCallback === 'function') {
        try {
          el.connectedCallback();
        } catch (_) {
          // Ignore component init edge-case failures
        }
      }
    });
  });
}

function triggerLazyMediaLoad(scope) {
  const hasLazyMedia = scope.querySelector('[data-lazy-image]:not(.loaded), [data-lazy-video]:not(.loaded)');
  if (!hasLazyMedia) return;

  ThemeBus.dispatchEvent(new Event(EVENTS.gridUpdated));

  if (typeof window.LazyImageLoader === 'function') {
    window.__lazyImageLoaderActive = false;
    requestAnimationFrame(() => {
      try {
        new window.LazyImageLoader();
      } catch (_) {
        // No-op if lazy loader bootstrap fails in this context.
      }
    });
  }
}

async function openQuickBuyModal(handle, trigger) {
  const modal = document.querySelector(`[data-modal-id="${QUICK_BUY_MODAL_ID}"]`);
  const modalBody = modal?.querySelector('[data-modal-body]');

  if (!modalBody) {
    window.location.href = productUrlByHandle(handle);
    return;
  }

  modalBody.innerHTML = '<p class="text-sm text-ink-secondary">Loading...</p>';
  ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalOpen, { detail: { id: QUICK_BUY_MODAL_ID } }));
  ThemeBus.dispatchEvent(new CustomEvent(EVENTS.quickBuyFormOpen, { detail: { id: handle } }));

  setTriggerLoading(trigger, true);

  try {
    const response = await fetch(sectionFetchUrl(handle), { method: 'GET' });
    if (!response.ok) throw new Error('Failed section fetch');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sectionWrapper = doc.querySelector('.shopify-section') || doc.body;
    if (!sectionWrapper) throw new Error('Missing section wrapper');

    await executeInjectedScripts(sectionWrapper);
    modalBody.innerHTML = sectionWrapper.innerHTML;
    reinitializeInjectedElements(modalBody);
    triggerLazyMediaLoad(modalBody);
  } catch (_) {
    window.location.href = productUrlByHandle(handle);
  } finally {
    setTriggerLoading(trigger, false);
  }
}

function initQuickBuyButtons() {
  if (window.__quickBuyButtonsInitialized) return;
  window.__quickBuyButtonsInitialized = true;

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-quick-buy-trigger]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const handle = trigger.dataset.productHandle;
    const singleVariant = trigger.dataset.singleVariant === 'true';
    const forceModal = trigger.dataset.forceModal === 'true';
    const variantId = Number(trigger.dataset.variantId || 0);

    if (!handle) return;

    if (singleVariant && !forceModal) {
      await directAddToCart(variantId, trigger);
      return;
    }

    await openQuickBuyModal(handle, trigger);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuickBuyButtons);
} else {
  initQuickBuyButtons();
}
